/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { merge } from 'lodash';
import { CMSLayout } from '.';
import {
  TArticleSchema,
  TAuthor,
} from '../../../constants/componentStaticData';
import {
  DOMAIN_PREFIX,
  LANGUAGE_CODE,
  LANGUAGE_ENUMS,
  domainCode,
} from '../../../constants/staticPageConstants';
import { CorporatePage, DefaultMeta } from '../../../types/partner-page-types';
import { processURL } from '../../../utils/static-pages/urlProcessor';
import { handleTrack } from '../../../utils/tracker';
import { ALIGN, SocialMedia } from '../../Author/SocialMedia';
import { DOMAIN_HOST_URL_PREFIX } from '../../Author/constants';
import CalculatorLayout from '../../Calculators/calculator-revamp/components/CalculatorLayout';
import GlossaryTerms from '../../glossary_v2/components/TermsPage';
import { PartnerPages } from '../../partner-pages/page';
import CMSPageDataServices from '../services/cms-pages.service';
import { AICMSLayout } from './AiCMSLayout';
import { LANG_DATA } from './language/dataset';
import {
  CATEGORY_PROPS,
  IAuthorDetails,
  IFAQ,
  IRELATED_POSTS,
  ISINGLE_POST,
  ISTATIC_PAGE_PROPS,
  PostData,
  THEME,
} from './types';
const shouldExcludeBreadcrumb = (
  domain: number,
  language: string,
  slug: string
) => {
  return (
    domain === domainCode.cleartaxAE &&
    language === LANGUAGE_ENUMS.English &&
    slug === 'calculators'
  );
};

export const transformBreadcrumb = (
  category: CATEGORY_PROPS,
  pageData: ISINGLE_POST,
  language: string
) => {
  const domainLinks = {
    [domainCode.cleartaxMY]: (language: string) =>
      language === 'English' ? '/my/en' : '/my/',
    [domainCode.cleartaxSA]: () => '/sa',
    [domainCode.cleartaxSG]: () => '/sg/e-invoicing-software',
    [domainCode.cleartaxAE]: () => '/ae',
    [domainCode.cleartaxPL]: () => '/pl',
    [domainCode.cleartaxOM]: () => '/om',
    [domainCode.cleartaxFR]: () => '/fr',
    [domainCode.cleartaxES]: () => '/es',
    [domainCode.cleartaxBH]: () => '/bh',
    [domainCode.cleartaxIn]: () => '/',
    [domainCode.cleartaxBE]: () => '/be',
    [domainCode.cleartaxKR]: () => '/kr',
    [domainCode.cleartaxJP]: () => '/jp',
    [domainCode.cleartaxNZ]: () => '/nz',
    [domainCode.cleartaxPH]: () => '/ph',
    [domainCode.cleartaxAL]: () => '/sq',
    [domainCode.cleartaxJO]: () => '/jo',
  };

  const breadcrumbData = [];
  const defaultLink = '/electronic-invoicing';

  const getLink = domainLinks[category.domain];

  const link = getLink ? getLink(language) : defaultLink;
  breadcrumbData.push({
    title: `${LANG_DATA[language]?.breadcrumb.home}`,
    link: link,
  });
  function generateData(data: CATEGORY_PROPS) {
    const route =
      data.slug === 'calculators' ? '' : DOMAIN_PREFIX[pageData.domain]?.route;
    const linkAddress = `${route}/${processURL(
      data.slug,
      language,
      pageData.domain
    )}`;

    if (data.parent) {
      generateData(data.parent);
    }
    if (!shouldExcludeBreadcrumb(pageData.domain, language, data.slug)) {
      breadcrumbData.push({
        title: data.name || '',
        link:
          data.parent && pageData.page_type === 'aggregation_page'
            ? ''
            : linkAddress,
      });
    }
  }
  generateData(category);
  if (pageData.page_type !== 'aggregation_page') {
    breadcrumbData.push({
      title: pageData.title,
      link: '',
    });
  }
  return breadcrumbData;
};

export const getBreadCrumbData = (data: ISINGLE_POST, lang: string) => {
  const language = data.domain === domainCode.cleartaxIn ? 'English' : lang;
  return transformBreadcrumb(data.category, data, language);
};

export const getGlossaryBreadCrumbData = (data: ISINGLE_POST, lang: string) => {
  const language = data.domain === domainCode.cleartaxIn ? 'English' : lang;
  return transformBreadcrumbGlossary(data.category, data, language);
};

const transformBreadcrumbWithDomain = (
  category: CATEGORY_PROPS,
  pageData: ISINGLE_POST,
  language: string
) => {
  const breadcrumbData = [
    { title: `${LANG_DATA[language]?.breadcrumb.home}`, link: '/' },
  ];

  function generateBreadCrumData(data: CATEGORY_PROPS) {
    const linkAddress = `https://${
      DOMAIN_PREFIX[pageData.domain]?.domain
    }/glossary/${processURL(data.slug, language, pageData.domain)}`;
    if (data.parent) {
      generateBreadCrumData(data.parent);
      breadcrumbData.push({
        title: data.name ? data.name.toUpperCase() : '',
        link: pageData.page_type !== 'aggregation_page' ? linkAddress : '',
      });
    } else {
      breadcrumbData.push({
        title: data.name ? data.name.toUpperCase() : '',
        link: linkAddress,
      });
    }
  }
  generateBreadCrumData(category);
  if (pageData.page_type !== 'aggregation_page') {
    breadcrumbData.push({
      title: pageData.title.toUpperCase(),
      link: '',
    });
  }
  return breadcrumbData;
};

const transformBreadcrumbGlossary = (
  category: CATEGORY_PROPS,
  pageData: ISINGLE_POST,
  language: string
) => {
  const breadcrumbData = [
    { title: `${LANG_DATA[language]?.breadcrumb.home}`, link: '/glossary' },
  ];

  function generateBreadCrumData(data: CATEGORY_PROPS) {
    const linkAddress = `https://${
      DOMAIN_PREFIX[pageData.domain]?.domain
    }/glossary/${processURL(data.slug, language, pageData.domain)}`;
    if (data.parent) {
      generateBreadCrumData(data.parent);
      breadcrumbData.push({
        title: data.name ? data.name.toUpperCase() : '',
        link: pageData.page_type !== 'aggregation_page' ? linkAddress : '',
      });
    } else {
      breadcrumbData.push({
        title: data.name ? data.name.toUpperCase() : '',
        link: linkAddress,
      });
    }
  }
  generateBreadCrumData(category);
  if (pageData.page_type !== 'aggregation_page') {
    breadcrumbData.push({
      title: pageData.title.toUpperCase(),
      link: '',
    });
  }
  return breadcrumbData;
};

export const getBreadCrumbDataWithDomain = (
  data: ISINGLE_POST,
  lang: string
) => {
  const language = data.domain === domainCode.cleartaxIn ? 'English' : lang;
  return transformBreadcrumbWithDomain(data.category, data, language);
};

export const ShareIcons = (props: {
  url: string;
  domain: number;
  language: string;
  align?: ALIGN;
}): JSX.Element => {
  const { url, domain, language, align = ALIGN.left } = props;

  const socialDomain = DOMAIN_PREFIX[domain];

  const whatsappLink = `whatsapp://send?text=https://${
    socialDomain?.host
  }/${processURL(url, language, domain)}`;
  const linkedInLink = `https://www.linkedin.com/shareArticle?mini=true&amp;amp;url=https://${
    socialDomain?.host
  }/${processURL(url, language, domain)}`;
  const facebookLink = `https://www.facebook.com/sharer/sharer.php?u=https://${
    socialDomain?.host
  }/${processURL(url, language, domain)}`;
  const twitterLink = `https://twitter.com/share?url=https://${
    socialDomain?.host
  }/${processURL(url, language, domain)}`;

  return (
    <SocialMedia
      linkedin={linkedInLink}
      facebook={facebookLink}
      whatsapp={whatsappLink}
      twitter={twitterLink}
      align={align}
    />
  );
};

export const createTOC = (DOM: HTMLElement | null) => {
  if (DOM) {
    const contentWrapper = document.createElement('div');
    //Add a header name for table
    const tocHeader = document.createElement('div');
    tocHeader.classList.add('tocHeader');

    const headers = DOM.querySelectorAll('h2, h3');
    // Create a list for the ToC entries

    contentWrapper.setAttribute('id', 'contentsOfTable');
    // toc?.removeChild(toc?.getElementById('contentsOfTable'));
    // toc?.appendChild(contentWrapper);

    const tocList = document.createElement('ul');
    for (let i = 0; i < headers.length; i++) {
      // to parse only heading present in content and rejection heading in FAQ and side CTA
      const curHeader = headers[i] as HTMLElement;
      if (
        !curHeader.classList.contains('accordion') &&
        !curHeader.classList.contains('uppercase')
      ) {
        // Create an id
        let name = `h${i}`;
        curHeader.id ? (name = curHeader.id) : (curHeader.id = name);

        //add span to header for adding top padding
        const hSpan = document.createElement('span');
        hSpan.setAttribute('id', name);
        // curHeader.id = '';
        curHeader.appendChild(hSpan);

        // a list item for the entry
        const tocListItem = document.createElement('li');

        // a link for the h2
        const tocEntry = document.createElement('a');
        // tocEntry.classList.add("anchor");
        tocEntry.setAttribute('href', '#' + name);
        tocEntry.innerText = curHeader.innerText;
        tocListItem.appendChild(tocEntry);

        // create sublist for sequence of H3 present
        // parse until next H2 is encountered and add it to nested ul
        const subList = document.createElement('ul');
        while (
          i + 1 < headers.length &&
          (headers[i + 1] as HTMLElement).nodeName === 'H3'
        ) {
          const nextHeader = headers[i + 1] as HTMLElement;
          let subName = 'h' + i;
          nextHeader.id ? (subName = nextHeader.id) : (nextHeader.id = subName);
          // a list item for the entry
          const tocSubListItem = document.createElement('li');

          //add span to header for adding top padding
          const hSpanTag = document.createElement('span');
          hSpanTag.setAttribute('id', subName);
          // curHeader.id = '';
          curHeader.appendChild(hSpanTag);

          // a link for the h3
          const tocSubEntry = document.createElement('a');
          // tocSubEntry.classList.add('anchor');
          tocSubEntry.setAttribute('href', '#' + subName);
          tocSubEntry.innerText = nextHeader.innerText;
          tocSubListItem.appendChild(tocSubEntry);
          subList.appendChild(tocSubListItem);
          tocListItem.appendChild(subList);
          i++;
        }

        tocList.appendChild(tocListItem);
      }
    }
    contentWrapper.appendChild(tocList);

    return contentWrapper;
  }

  return null;
};

export const createTOCNewTemplateReact = (DOM: HTMLElement | null) => {
  if (DOM) {
    const headers = DOM.querySelectorAll('h2, h3');
    const indexList = [];
    // sanitizing tags with empty content
    for (let i = 0; i < headers.length; i++) {
      const curHeader = headers[i] as HTMLElement;

      // Skip FAQ headings and accordion headers to prevent hydration issues
      if (
        curHeader.classList.contains('accordion') ||
        curHeader.classList.contains('faq-title') ||
        curHeader.classList.contains('uppercase')
      ) {
        continue;
      }

      const name = `h${i}`;
      if (curHeader.innerText.trim()) {
        curHeader.setAttribute('id', name);
        indexList.push({
          id: `h${i}`,
          content: curHeader.innerText,
        });
      }
    }

    return indexList;
  }
  return [];
};

// Helper function to process a single YouTube player
const processYouTubePlayer = (playerElement: HTMLElement) => {
  // --- Skip if already processed ---
  if (playerElement.classList.contains('youtube-processed')) {
    return;
  }

  // Extract video ID using the helper function
  const videoUrl = playerElement.dataset.id;
  if (!videoUrl) {
    console.warn(
      'YouTube player div missing data-id attribute:',
      playerElement
    );
    playerElement.classList.add('youtube-processed');
    return;
  }

  const videoID = getYouTubeVideoID(videoUrl);
  if (!videoID) {
    console.warn(
      'Could not extract YouTube Video ID from data-id:',
      videoUrl,
      playerElement
    );
    playerElement.classList.add('youtube-processed');
    return;
  }

  // --- Create Thumbnail ---
  const thumbnailUrl = `https://img.youtube.com/vi/${videoID}/hqdefault.jpg`;
  const thumbnailImg = document.createElement('img');
  thumbnailImg.src = thumbnailUrl;
  thumbnailImg.alt = 'YouTube Video Thumbnail';
  thumbnailImg.style.width = '100%';
  thumbnailImg.style.height = '100%';
  thumbnailImg.style.objectFit = 'cover';
  thumbnailImg.style.display = 'block';
  thumbnailImg.setAttribute('loading', 'lazy');

  // --- Create Play Button ---
  const playButton = document.createElement('div');
  playButton.setAttribute('aria-label', 'Play Video');
  playButton.style.position = 'absolute';
  playButton.style.top = '50%';
  playButton.style.left = '50%';
  playButton.style.transform = 'translate(-50%, -50%)';
  playButton.style.width = '68px';
  playButton.style.height = '48px';
  playButton.style.background = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 68 48"><path fill="rgba(33,33,33,0.8)" d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.63 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z"></path><path fill="white" d="M45 24L27 14v20"></path></svg>') center center no-repeat`;
  playButton.style.backgroundSize = 'contain';
  playButton.style.border = 'none';
  playButton.style.cursor = 'pointer';
  playButton.style.zIndex = '1';

  // --- Prepare Placeholder ---
  playerElement.style.position = 'relative';
  playerElement.style.overflow = 'hidden';
  playerElement.style.cursor = 'pointer';
  playerElement.innerHTML = '';
  playerElement.appendChild(thumbnailImg);
  playerElement.appendChild(playButton);

  // --- Add click listener to load iframe ---
  playerElement.onclick = function () {
    const clickedPlayer = this as HTMLElement;

    if (clickedPlayer.querySelector('iframe.tube-div')) {
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.setAttribute(
      'src',
      `https://www.youtube.com/embed/${videoID}?rel=0&autoplay=1&mute=1&enablejsapi=1`
    );
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('class', 'tube-div');
    iframe.setAttribute('id', `youtube-iframe-${videoID}`);
    iframe.setAttribute('title', 'YouTube video player');
    iframe.setAttribute(
      'style',
      'width: 100%; height: 100%; position: absolute; top: 0; left: 0;'
    );
    if (clickedPlayer.dataset.fullscreen == '1') {
      iframe.setAttribute('allowfullscreen', '');
    }

    clickedPlayer.innerHTML = '';
    clickedPlayer.appendChild(iframe);
    clickedPlayer.style.cursor = 'default';
  };

  playerElement.classList.add('youtube-processed');
};

export const optimiseYoutube = (DOM: HTMLElement | null) => {
  if (!DOM || typeof window === 'undefined') return;

  const youtubePlayers = DOM.getElementsByClassName('youtube-player');
  if (youtubePlayers.length === 0) return; // Skip if no videos on page

  // Convert to array for easier processing
  const players = Array.from(youtubePlayers) as HTMLElement[];

  // Process in batches to avoid long tasks that block main thread
  const processBatch = (startIndex: number, batchSize: number = 3) => {
    const endIndex = Math.min(startIndex + batchSize, players.length);

    for (let i = startIndex; i < endIndex; i++) {
      const player = players[i];
      if (!player.classList.contains('youtube-processed')) {
        processYouTubePlayer(player);
      }
    }

    // Schedule next batch if there are more items
    if (endIndex < players.length) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => processBatch(endIndex, batchSize), {
          timeout: 1000,
        });
      } else {
        setTimeout(() => processBatch(endIndex, batchSize), 50);
      }
    }
  };

  // Check if IntersectionObserver is available for below-fold content
  if ('IntersectionObserver' in window) {
    // Split into above-fold and below-fold
    const viewportHeight = window.innerHeight;
    const aboveFold: HTMLElement[] = [];
    const belowFold: HTMLElement[] = [];

    players.forEach((player) => {
      const rect = player.getBoundingClientRect();
      if (rect.top < viewportHeight + 100) {
        aboveFold.push(player);
      } else {
        belowFold.push(player);
      }
    });

    // Process above-fold items in batches during idle time
    if (aboveFold.length > 0) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(
          () => {
            aboveFold.forEach((player) => {
              if (!player.classList.contains('youtube-processed')) {
                processYouTubePlayer(player);
              }
            });
          },
          { timeout: 1000 }
        );
      } else {
        setTimeout(() => {
          aboveFold.forEach((player) => processYouTubePlayer(player));
        }, 100);
      }
    }

    // Use Intersection Observer for below-fold items
    if (belowFold.length > 0) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              processYouTubePlayer(entry.target as HTMLElement);
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '100px', threshold: 0.01 }
      );

      belowFold.forEach((player) => observer.observe(player));
    }

    return;
  }

  // Fallback: process in batches
  processBatch(0);
};

const getYouTubeVideoID = (url: string) => {
  if (!url) return null;

  // Patterns covering:
  // - https://www.youtube.com/watch?v=VIDEOID
  // - https://youtu.be/VIDEOID
  // - https://www.youtube.com/embed/VIDEOID
  // - https://youtube.com/shorts/VIDEOID
  // and some with extra parameters

  const regex =
    /(?:youtube(?:-nocookie)?\.com\/(?:[^/]+\/.+\/|(?:v|embed|shorts)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

  const match = url.match(regex);
  return match ? match[1] : null;
};

/**
 * BUILD-TIME: Pre-process YouTube players at build time
 * Transforms <div class="youtube-player" data-id="..."> into fully rendered HTML
 * This eliminates ALL runtime DOM manipulation overhead
 */
const preProcessYouTubePlayers = (htmlContent: string): string => {
  if (!htmlContent) return htmlContent;

  // Match all youtube-player divs
  const youtubeRegex =
    /<div[^>]*class="[^"]*youtube-player[^"]*"[^>]*data-id="([^"]+)"[^>]*><\/div>/g;

  return htmlContent.replace(youtubeRegex, (match, videoUrl) => {
    const videoID = getYouTubeVideoID(videoUrl);
    if (!videoID) return match;

    const thumbnailUrl = `https://img.youtube.com/vi/${videoID}/hqdefault.jpg`;

    // Generate complete HTML at build time
    return `<div class="youtube-player youtube-processed" data-id="${videoUrl}" style="position: relative; overflow: hidden; cursor: pointer;">
      <img src="${thumbnailUrl}" alt="YouTube Video Thumbnail" style="width: 100%; height: 100%; object-fit: cover; display: block;" loading="lazy">
      <div aria-label="Play Video" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 68px; height: 48px; background: url('data:image/svg+xml;utf8,<svg xmlns=\\"http://www.w3.org/2000/svg\\" viewBox=\\"0 0 68 48\\"><path fill=\\"rgba(33,33,33,0.8)\\" d=\\"M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.63 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z\\"></path><path fill=\\"white\\" d=\\"M45 24L27 14v20\\"></path></svg>') center center no-repeat; background-size: contain; border: none; cursor: pointer; z-index: 1;" onclick="(function(){const iframe=document.createElement('iframe');iframe.setAttribute('src','https://www.youtube.com/embed/${videoID}?rel=0&autoplay=1&mute=1&enablejsapi=1');iframe.setAttribute('frameborder','0');iframe.setAttribute('class','tube-div');iframe.setAttribute('id','youtube-iframe-${videoID}');iframe.setAttribute('title','YouTube video player');iframe.setAttribute('style','width: 100%; height: 100%; position: absolute; top: 0; left: 0;');iframe.setAttribute('allowfullscreen','');this.parentElement.innerHTML='';this.parentElement.appendChild(iframe);this.parentElement.style.cursor='default';}).call(this)"></div>
    </div>`;
  });
};

const setInnerHTML = (elm: HTMLElement, html: string) => {
  elm.innerHTML = html;
  elm.querySelector('.rc-component a')?.addEventListener('click', () => {
    handleTrack({ label: 'content_inline_file_now' });
  });
  Array.from(elm.querySelectorAll('script')).forEach((oldScriptEl) => {
    const newScriptEl = document.createElement('script');

    Array.from(oldScriptEl.attributes).forEach((attr) => {
      newScriptEl.setAttribute(attr.name, attr.value);
    });

    const scriptText = document.createTextNode(oldScriptEl.innerHTML);
    newScriptEl.appendChild(scriptText);

    oldScriptEl?.parentNode &&
      oldScriptEl.parentNode.replaceChild(newScriptEl, oldScriptEl);
  });
};

const getComponent = async (comp: HTMLElement) => {
  try {
    const res = await CMSPageDataServices.getReusableComponent(comp.id);
    const listData = await res.data;
    setInnerHTML(comp, listData?.content || '');
    // comp.innerHTML = listData?.content || '';
  } catch (error) {
    console.error('Error fetching reusable component:', error);
    // Gracefully handle error - component remains unchanged
  }
};

/** Function that gets all the reusable components based on classname
 * The ids are then seached for and addedn into the dom after API res
 * Uses smart loading strategy based on viewport position
 * @internal */
export const ReusableComponent = (DOM: HTMLElement | null) => {
  if (!DOM || typeof window === 'undefined') return;

  const components = DOM.querySelectorAll('.rc-component:not(.isCrawlable)');
  if (components.length === 0) return; // Skip if no reusable components on page

  const comps = Array.from(components) as HTMLElement[];

  // Process components in batches to avoid blocking
  const processBatch = async (items: HTMLElement[], batchSize: number = 2) => {
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);

      // Process batch
      await Promise.all(
        batch.map((comp) => {
          if (!comp.dataset.rcLoading) {
            comp.dataset.rcLoading = 'true';
            return getComponent(comp);
          }
          return Promise.resolve();
        })
      );

      // Yield to main thread between batches
      if (i + batchSize < items.length) {
        await new Promise((resolve) => {
          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => resolve(null), { timeout: 500 });
          } else {
            setTimeout(resolve, 50);
          }
        });
      }
    }
  };

  // Use Intersection Observer for better performance
  if ('IntersectionObserver' in window) {
    const viewportHeight = window.innerHeight;
    const aboveFold: HTMLElement[] = [];
    const belowFold: HTMLElement[] = [];

    comps.forEach((comp) => {
      const rect = comp.getBoundingClientRect();
      if (rect.top < viewportHeight + 200) {
        aboveFold.push(comp);
      } else {
        belowFold.push(comp);
      }
    });

    // Load above-fold components during idle time in batches
    if (aboveFold.length > 0) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => processBatch(aboveFold, 2), {
          timeout: 1000,
        });
      } else {
        setTimeout(() => processBatch(aboveFold, 2), 200);
      }
    }

    // Use Intersection Observer for below-fold components
    if (belowFold.length > 0) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const comp = entry.target as HTMLElement;
              if (!comp.dataset.rcLoading) {
                comp.dataset.rcLoading = 'true';
                getComponent(comp);
                observer.unobserve(comp);
              }
            }
          });
        },
        { rootMargin: '200px', threshold: 0.01 }
      );

      belowFold.forEach((comp) => observer.observe(comp));
    }

    return;
  }

  // Fallback: process in batches
  processBatch(comps, 2);
};

/**
 * Adds loading="lazy" and decoding="async" to img tags within an HTML string.
 * Avoids adding if the attributes already exist or src is missing.
 * @param htmlString The raw HTML string.
 * @returns The processed HTML string with lazy loading attributes added to images.
 */
export const addLazyLoadAttributesToHtmlString = (
  htmlString: string
): string => {
  if (!htmlString) return '';

  let isFirstImage = true; // Track first image for LCP optimization

  // Regex to find <img> tags, capturing existing attributes. Handles self-closing tags.
  // It specifically looks for a src attribute to avoid matching invalid img tags.
  const imgRegex = /<img\s+([^>]*?\s)?src\s*=\s*(['"])(.*?)\2([^>]*?)(\/?)>/gi;

  return htmlString.replace(
    imgRegex,
    (
      match,
      preSrcAttrs = '', // Attributes before src
      quote: string,
      srcValue: string,
      postSrcAttrs = '', // Attributes after src
      selfClosing = ''
    ) => {
      const existingAttrs = (preSrcAttrs || '') + (postSrcAttrs || '');
      // Check if attributes already exist
      const hasLoading = /\sloading\s*=\s*['"]/.test(existingAttrs);
      const hasDecoding = /\sdecoding\s*=\s*['"]/.test(existingAttrs);
      const hasFetchPriority = /\sfetchpriority\s*=\s*['"]/.test(existingAttrs);

      let loadingAttr = '';
      let decodingAttr = '';
      let fetchPriorityAttr = '';

      // First image gets priority treatment (likely LCP element)
      if (isFirstImage) {
        if (!hasLoading) {
          loadingAttr = ' loading="eager"';
        }
        if (!hasFetchPriority) {
          fetchPriorityAttr = ' fetchpriority="high"';
        }
        isFirstImage = false;
      } else {
        // All other images lazy load
        if (!hasLoading) {
          loadingAttr = ' loading="lazy"';
        }
      }

      if (!hasDecoding) {
        decodingAttr = ' decoding="async"';
      }

      // Reconstruct the tag, ensuring attributes are trimmed and src is preserved correctly
      return `<img ${preSrcAttrs || ''}src=${quote}${srcValue}${quote}${
        postSrcAttrs || ''
      }${loadingAttr}${decodingAttr}${fetchPriorityAttr}${selfClosing}>`;
    }
  );
};

const patternsToReplace = [
  ';color:#',
  'color:rgb',
  'margin:',
  'margin-top:',
  'margin-right:',
  'margin-bottom:',
  'margin-left:',
  'font-family:',
  'width:',
];

const stripOutsideRC = (root: HTMLElement | null) => {
  if (!root || typeof window === 'undefined') return;

  const combinedPattern = new RegExp(patternsToReplace.join('|'), 'g');

  const elements = root.querySelectorAll('[style], style');

  elements.forEach((el) => {
    //No stripping for an isCrawlable rc-component or its children
    if (el.closest('.rc-component.isCrawlable')) return;

    // Inline style=""
    if (el.hasAttribute('style')) {
      const style = el.getAttribute('style') || '';
      const cleaned = style.replace(combinedPattern, '');

      if (cleaned.trim()) {
        el.setAttribute('style', cleaned.trim());
      } else {
        el.removeAttribute('style');
      }
    }

    // <style> tag
    if (el.tagName === 'STYLE') {
      el.innerHTML = el.innerHTML.replace(combinedPattern, '');
    }
  });
};

export const stripUnwantedStyles = (dom: string) => {
  const combinedPattern = new RegExp(patternsToReplace.join('|'), 'g');

  return dom.replace(combinedPattern, '');
};
export const stripUnwantedtags = (dom: string) => {
  const tagsToRemove = [
    'p',
    'strong',
    'span',
    'meta',
    'br',
    'div',
    'ul',
    'li',
    'u',
    'a',
    'ol',
    'sup',
  ];
  const patternsToReplace = tagsToRemove.map((tag) => `</?${tag}[^>]*>`);
  const combinedPattern = new RegExp(patternsToReplace.join('|'), 'g');
  return dom.replace(combinedPattern, '');
};

export const decodeHtml = (encodedString: string) => {
  return encodedString.replace(/&#(\d+);/gi, function (_match, numStr) {
    const num = parseInt(numStr, 10);
    return String.fromCharCode(num);
  });
};

export const getTextColor = (theme: THEME) =>
  theme === THEME.dark ? 'text-white' : 'text-gray-700';

export const getBgColor = (theme: THEME) =>
  theme === THEME.dark ? 'bg-black' : 'bg-white';

//TODO: update to a reusable function

type TArticleSchemaData = {
  headline: string;
  description: string;
  articleSection: string;
  keywords: string;
  image?: { url: string };
  datePublished: string;
  dateModified: string;
  author?: Partial<TAuthor>;
  mainEntityOfPage: { '@id': string };
  publisher?: {
    name: string;
    logo: {
      url: string;
    };
  };
};

export const getArticleSchema = (
  ARTICLE_SCHEMA: TArticleSchema,
  data: TArticleSchemaData
) => {
  return merge(ARTICLE_SCHEMA, data);
};

type TArticleAuthorParams = {
  author_details?: IAuthorDetails;
};

const getArticleAuthorDetails = ({
  author_details,
}: TArticleAuthorParams): { author: Partial<TAuthor> } | null => {
  if (!author_details?.status) return null;
  return {
    author: {
      name: author_details?.name,
      url: `${DOMAIN_HOST_URL_PREFIX}/authors/${author_details?.slug}`,
    },
  };
};

export const getArticleSchemaData = (
  data: ISINGLE_POST,
  curr_page_url: string
): TArticleSchemaData => {
  return {
    headline: data.title,
    description: data.description,
    articleSection: data.category.name,
    keywords: data.keywords || '',
    datePublished: data.created_at,
    dateModified: data.updated_at,
    mainEntityOfPage: { '@id': curr_page_url },
    ...getArticleAuthorDetails({
      author_details: data.author_details,
    }),
  };
};

export const getArticles = (
  articles: Array<IRELATED_POSTS> | null,
  heading: string
): JSX.Element => {
  if (!Array.isArray(articles) || articles === null || articles?.length === 0)
    return <></>;

  return (
    <>
      <h4 className="text-s-12 mb-5 font-semibold uppercase sidebar-title mt-12">
        {heading}
      </h4>
      <ul className="subcategories">
        {articles.map((ele: IRELATED_POSTS) => {
          return (
            <li key={ele.title}>
              <a href={ele.permalink} title={ele.title}>
                {decodeHtml(ele.title)}
              </a>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export const generateComponent = ({
  postData,
  pageType,
  metaData,
}: ISTATIC_PAGE_PROPS) => {
  if (pageType === 'CMS') {
    if (
      'data' in postData &&
      postData.data.domain === 1 &&
      postData.data.language === LANGUAGE_CODE['English']
    ) {
      return (
        <div>
          <AICMSLayout
            data={(postData as PostData).data}
            modifiedDate={(postData as PostData).data.updated_at}
          />
        </div>
      );
    }
    return (
      <CMSLayout
        data={(postData as PostData).data}
        modifiedDate={(postData as PostData).data.updated_at}
      />
    );
  } else if (pageType === 'partner') {
    return (
      <PartnerPages
        postData={postData as unknown as CorporatePage}
        metaData={metaData as unknown as DefaultMeta}
      />
    );
  } else if (pageType === 'glossary') {
    return <GlossaryTerms postData={postData as unknown as PostData} />;
  } else if (pageType === 'calculator') {
    return <CalculatorLayout postData={postData as PostData} />;
  } else {
    return <div>no data</div>;
  }
};

export const getActiveFAQsInFAQFormat = (data: IFAQ[]) => {
  if (!data || !Array.isArray(data)) return [];

  const filteredData = data.filter((item) => item.status);
  return filteredData.map((item) => ({
    header: item.question,
    content: item.answer,
  }));
};
